import React from 'react';
import PropTypes from 'prop-types';

import { crudConfig, uiConfig } from '../../conf/securitySchemes';

// // API Hooks
import { useSecuritySchemes } from '../api/hooks/useSecuritySchemes';

// UI Components
import { Button } from '../../../components/Button';
import Table from '../../../components/Table';
import { AddEditScheme } from '../components/addEditScheme';
import { DeleteScheme } from '../components/deleteScheme';

import { translateUIConfigForTable, translateUIConfigForAddEdit } from '../../../common/utility';

const { READ_ONLY_FEATURES } = process.env;

const InternalSecurityEditor = ({ userContext }) => {
    const { sessionToken } = userContext;
    const [editing, setEditing] = React.useState(null);
    const [deleting, setDeleting] = React.useState(null);
    const saveDisabled = READ_ONLY_FEATURES === 'true';
    const { data, isLoading } = useSecuritySchemes(sessionToken);
    const { columns, actions } = translateUIConfigForTable(uiConfig, setEditing, setDeleting);
    const tableData = React.useMemo(() => {
        if (!data) return [];
        if (actions != null) {
            return actions != null
                ? data.map((v) => ({
                      ...v,
                      DataTableActions: actions.map((i) => ({
                          ...i,
                          action: () => i.action(v[uiConfig.primaryKey])
                      }))
                  }))
                : data;
        } else {
            return data;
        }
    }, [data, actions]);

    const getItemById = React.useCallback(
        (id) => {
            return tableData.find((item) => item[uiConfig.primaryKey] === id);
        },
        [tableData]
    );

    const fetchDeleteData = () => {
        return getItemById(deleting);
    };

    const closeAddEdit = () => {
        setEditing(null);
    };

    const closeDelete = () => {
        setDeleting(null);
    };
    const isExistingDoc = (doc) => {
        return (
            (data ?? []).find((v) => {
                return v.schemeId !== doc.schemeId && v.schemeName === doc.schemeName;
            }) !== undefined
        );
    };

    const controlButtons = [];
    if (crudConfig.functions.insert) {
        controlButtons.push(
            <Button key={'add-new'} appearance="primary" onClick={() => setEditing('new')}>
                Add New Security Scheme
            </Button>
        );
    }

    return (
        <>
            <Table
                withHeader={true}
                columns={columns}
                data={tableData}
                withActions={actions !== null}
                id={'internal-security-editor'}
                label={'Internal CAT Security Editor'}
                showControlDeck={true}
                paginated={true}
                loading={isLoading}
                controlButtons={controlButtons}
            />
            {editing != null && (
                <AddEditScheme
                    formModel={translateUIConfigForAddEdit(uiConfig)}
                    saveDisabled={saveDisabled}
                    selectedSchemeId={editing}
                    closeDialog={closeAddEdit}
                    sessionToken={sessionToken}
                    isExistingDoc={isExistingDoc}
                    onMount={typeof uiConfig.onFormMount === 'function' ? uiConfig.onFormMount : undefined}
                />
            )}
            {deleting != null ? (
                <DeleteScheme data={fetchDeleteData()} closeDialog={closeDelete} sessionToken={sessionToken} />
            ) : null}
        </>
    );
};

export default InternalSecurityEditor;

InternalSecurityEditor.propTypes = {
    userContext: PropTypes.object
};
